// Domain contract shared by the Functions, the backoffice and (mirrored in
// Dart) the inspection app. Firestore layout: talleres/{tallerId}/...
// Timestamps written by the server are Firestore Timestamps; device-side
// times (capture, signature) are ISO strings because they come from the
// device clock and must be read as such in a dispute.
import { z } from 'zod';
import { estadoAccesorio, estadoZona, motivoIngreso, tipoInspeccion } from './catalogo.ts';
import { colorDocumento, esHexColor, HEX_COLOR } from './color.ts';
import { campoFichaComercial, reglasFicha } from './comercial.ts';
import { configFotos } from './fotos.ts';
import {
  esquemaEnviarCorreoTipo,
  esquemaGuardarEnlacesCorreo,
  esquemaGuardarPlantillaCorreo,
} from './correosTipo.ts';
import { esquemaInvitarAPrueba } from './invitacion.ts';
import { limpiarRut, validarRut } from './rut.ts';
import { versionTerminos } from './terminos.ts';

/**
 * `equipo` is a shared device account (a workshop tablet): it signs in with
 * e-mail and password but has no access to workshop data. People sign in on
 * it with their PIN (loginPin), getting a session of their shadow account
 * (`pin_<uid>`) whose `persona` claim names them.
 */
export const rol = z.enum(['recepcion', 'jefe_taller', 'admin', 'superadmin', 'equipo']);
export type Rol = z.infer<typeof rol>;

/** Roles of a person of the workshop (what an admin can assign). */
export const rolPersona = z.enum(['recepcion', 'jefe_taller', 'admin']);
export type RolPersona = z.infer<typeof rolPersona>;

/** Normalized plate: uppercase letters and digits only (GKSB78). */
export const patente = z.string().regex(/^[A-Z0-9]{5,8}$/);
export const normalizarPatente = (p: string) => p.toUpperCase().replace(/[^A-Z0-9]/g, '');

const fechaIso = z.iso.datetime({ offset: true });
/** Plates kept per customer (firestore.rules caps the list at the same size). */
export const MAX_PATENTES_CLIENTE = 500;
const idFoto = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/);

export const foto = z.object({
  id: idFoto,
  zona: z.string(),
  /** 'despues' = "after" photo taken at delivery/re-entry. */
  momento: z.enum(['antes', 'despues']),
  ruta: z.string(),
  formato: z.enum(['jpeg', 'webp']),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  bytes: z.number().int().positive(),
  ancho: z.number().int().positive(),
  alto: z.number().int().positive(),
  capturadaEn: fechaIso,
  /** Laplacian variance at 1024 px (same algorithm as tools/calibracion-fotos). */
  nitidez: z.number().nonnegative(),
  /** True when the user kept a photo flagged as blurry/dark. */
  forzada: z.boolean(),
});
export type Foto = z.infer<typeof foto>;

export const zonaInspeccion = z.object({
  estado: estadoZona.nullable(),
  fotoIds: z.array(idFoto),
  nota: z.string().max(500).optional(),
  /** Delivery/re-entry comparison against the reception. */
  entrega: z.enum(['sin_cambio', 'reparado', 'nuevo']).optional(),
});

/** One legal notice the customer accepts when signing. */
export const LARGO_MAX_AVISO_LEGAL = 600;
export const MAX_AVISOS_LEGALES = 8;
export const textoAvisoLegal = z
  .string()
  .trim()
  .min(1, 'El aviso no puede quedar vacío')
  .max(LARGO_MAX_AVISO_LEGAL, `Cada aviso admite hasta ${LARGO_MAX_AVISO_LEGAL} caracteres`);
export const listaAvisosLegales = z
  .array(textoAvisoLegal)
  .min(1, 'Deja al menos un aviso o restaura los textos por defecto')
  .max(MAX_AVISOS_LEGALES, `Hasta ${MAX_AVISOS_LEGALES} avisos`);
/**
 * The workshop's own legal notices by inspection type (a siniestro has
 * none). A missing type uses the default text of catalogo.avisosLegales.
 */
export const avisosLegalesTaller = z.strictObject({
  recepcion: listaAvisosLegales.optional(),
  reingreso: listaAvisosLegales.optional(),
  entrega: listaAvisosLegales.optional(),
});
export type AvisosLegalesTaller = z.infer<typeof avisosLegalesTaller>;

export const firma = z.object({
  /** SVG path data of the strokes, in a ancho×alto box. Vector: small and exact. */
  trazos: z.string().min(1),
  ancho: z.number().positive(),
  alto: z.number().positive(),
  nombre: z.string().min(1),
  rut: z.string().optional(),
  en: fechaIso,
});

/**
 * `firmada` = closed and immutable. A siniestro may close without the
 * customer's signature (it is optional there), but it is still `firmada`.
 */
export const estadoInspeccion = z.enum(['borrador', 'firmada', 'anulada']);

/** Insurance data of a siniestro inspection: all optional (filled when known). */
export const seguro = z.object({
  compania: z.string().trim().max(120).optional(),
  numeroSiniestro: z.string().trim().max(60).optional(),
  liquidador: z.string().trim().max(120).optional(),
});
export type Seguro = z.infer<typeof seguro>;

export const inspeccion = z.object({
  tallerId: z.string(),
  tipo: tipoInspeccion,
  estado: estadoInspeccion,
  creadaPor: z.string(),
  dispositivoId: z.string().optional(),
  /** For entrega/reingreso: the reception it is compared against. */
  recepcionId: z.string().optional(),
  /** For a recepcion (Ingreso) started from a siniestro: compared against it. */
  siniestroId: z.string().optional(),
  ordenId: z.string().optional(),
  patente,
  vehiculo: z.object({
    marca: z.string(),
    modelo: z.string(),
    anio: z.number().int().optional(),
    color: z.string(),
    km: z.number().int().nonnegative().optional(),
  }),
  cliente: z.object({
    nombre: z.string().min(1),
    rut: z.string(),
    telefono: z.string().optional(),
    correo: z.string().optional(),
  }),
  motivo: motivoIngreso,
  aseguradora: z.string().optional(),
  nroSiniestro: z.string().optional(),
  /** Only for tipo siniestro. */
  seguro: seguro.optional(),
  /** Index into catalogo.nivelesCombustible (0..7). */
  combustible: z.number().int().min(0).max(7).nullable(),
  zonas: z.record(z.string(), zonaInspeccion),
  fotos: z.record(z.string(), foto),
  /** Mirror of Object.keys(fotos): Storage rules can only check list membership. */
  fotoIds: z.array(idFoto),
  accesorios: z.record(z.string(), estadoAccesorio),
  observaciones: z.string().max(2000).optional(),
  objetosValor: z.string().max(1000).optional(),
  avisosAceptados: z.boolean(),
  /**
   * Frozen copy of the legal notices exactly as shown to and accepted by the
   * customer (the workshop's own wording at that moment, or the default).
   * Never a reference to the config: editing the config later must not
   * change a signed inspection. Missing (signed before this field existed,
   * or a siniestro) = the default text of catalogo.avisosLegales.
   */
  avisosLegales: listaAvisosLegales.optional(),
  firma: firma.optional(),
  firmaRetiro: firma.optional(),
  /**
   * Device clock, UTC ISO ("...Z") so it sorts as a string: the app lists
   * "today" with a range query and it must work offline (no serverTimestamp).
   */
  creadaEn: fechaIso.optional(),
  /** Device clock when the customer signed (ISO with offset). */
  firmadaEn: fechaIso.optional(),
});
export type Inspeccion = z.infer<typeof inspeccion>;

/**
 * Id of `talleres/{t}/clientes/{id}`: the RUT normalized with limpiarRut
 * ("12.345.678-5" -> "123456785"), so the app finds a customer by RUT with a
 * direct get, whatever way it was typed.
 */
export const idCliente = (rut: string) => limpiarRut(rut);
export const ID_CLIENTE = /^\d{1,8}[0-9K]$/;

/**
 * A workshop customer, keyed by idCliente(rut). Written only by the
 * inspection app when step 1 is completed (merge, like vehiculos/{patente}):
 * the latest contact data plus every plate seen with this RUT (arrayUnion).
 * `actualizado` is the device clock (UTC ISO, as written offline).
 */
export const cliente = z.object({
  nombre: z.string().trim().min(1).max(120),
  /** Formatted as in the inspection ("12.345.678-5"). */
  rut: z.string().max(12).refine(validarRut, 'RUT inválido'),
  telefono: z.string().max(30).optional(),
  correo: z.string().max(254).optional(),
  patentes: z.array(patente).min(1).max(MAX_PATENTES_CLIENTE),
  actualizado: fechaIso,
});
export type Cliente = z.infer<typeof cliente>;

export const discrepancia = z.object({
  zona: z.string(),
  antes: estadoZona.nullable(),
  despues: estadoZona.nullable(),
  tipo: z.enum(['dano_nuevo', 'empeora', 'reparado']),
});
export type Discrepancia = z.infer<typeof discrepancia>;

export const repuesto = z.object({
  nombre: z.string().min(1),
  proveedor: z.string().optional(),
  llegada: z.string().optional(),
  estado: z.enum(['pendiente', 'pedido', 'recibido']),
});

/** Server Timestamp as seen by any SDK (admin or web): seconds + nanoseconds. */
export const marcaTiempo = z.object({ seconds: z.number(), nanoseconds: z.number() });

export const orden = z.object({
  patente,
  inspeccionId: z.string(),
  cliente: z.object({ nombre: z.string(), telefono: z.string().optional() }),
  vehiculo: z.string(),
  /** Current phase; while in the neutral zone, the phase it left. */
  faseIdx: z.number().int().min(0),
  faseDesde: marcaTiempo,
  /** In the neutral zone the car waits (FIFO by colaDesde) for faseDestino. */
  enZonaNeutral: z.boolean(),
  faseDestino: z.number().int().min(0).nullable(),
  colaDesde: marcaTiempo.nullable(),
  repuestos: z.array(repuesto),
  /** While waiting for parts: kept at the workshop or returned to the customer. */
  retencion: z.enum(['retenido', 'devuelto']).nullable(),
  /** Finished (left the Entrega phase): no longer occupies a slot. */
  cerrada: z.boolean(),
  creadaEn: marcaTiempo,
});
export type Orden = z.infer<typeof orden>;

const hexColor = z.string().regex(HEX_COLOR);

/**
 * Interface color mode (ausente = 'tallerza'):
 *  - tallerza: the platform colors (packages/tokens);
 *  - logo: primario/acento taken from the logo palette (temaDesdePaleta);
 *  - personalizado: primario/acento picked by hand (starting from the logo).
 * primario/acento are stored as chosen; every consumer paints them through
 * variantesTema (shared/src/color.ts), which guarantees contrast. They are
 * kept in 'tallerza' mode too, so switching back restores them.
 */
export const modoColorTaller = z.enum(['tallerza', 'logo', 'personalizado']);
export type ModoColorTaller = z.infer<typeof modoColorTaller>;

export const interfazTaller = z
  .object({
    modo: modoColorTaller,
    primario: hexColor.optional(),
    acento: hexColor.optional(),
  })
  .refine((x) => x.modo === 'tallerza' || (x.primario !== undefined && x.acento !== undefined), {
    message: 'Los modos logo y personalizado necesitan primario y acento',
  });
export type InterfazTaller = z.infer<typeof interfazTaller>;

/**
 * Workshop branding: logo path (talleres/{t}/marca/...), the document color
 * (#RRGGBB, receipt/PDF), the palette extracted from the logo and the
 * interface color mode. With interfaz in 'logo' or 'personalizado' mode the
 * document color must be colorDocumento(primario): the primario darkened to
 * ≥ 4.5:1 on white paper (the accent only guarantees 3:1, too little for
 * the PDF's small text and thin rules).
 */
export const marcaTaller = z
  .object({
    logo: z
      .string()
      .regex(/^talleres\/[A-Za-z0-9_-]+\/marca\/[A-Za-z0-9._-]+$/)
      .nullable(),
    color: hexColor,
    /** Up to 6 colors extracted from the logo (extraerPaleta), most relevant first. */
    paleta: z.array(hexColor).max(6).optional(),
    interfaz: interfazTaller.optional(),
  })
  .refine(
    (m) =>
      // zod runs this even when a nested field failed: never feed it bad hex.
      !esHexColor(m.interfaz?.primario) ||
      !esHexColor(m.color) ||
      m.interfaz.modo === 'tallerza' ||
      m.color.toUpperCase() === colorDocumento(m.interfaz.primario),
    {
      message: 'El color del documento debe ser el primario legible sobre blanco',
      path: ['color'],
    },
  );
export type MarcaTaller = z.infer<typeof marcaTaller>;

/**
 * The logo path only if it lives in this workshop's own `marca/` folder.
 * The server reads it with the Admin SDK (bypassing storage.rules), so a
 * path pointing at another workshop's evidence must never be followed.
 */
export function logoDelTaller(tallerId: string, logo: string | null | undefined): string | null {
  if (!logo) return null;
  const prefijo = `talleres/${tallerId}/marca/`;
  if (!logo.startsWith(prefijo)) return null;
  return /^[A-Za-z0-9._-]+$/.test(logo.slice(prefijo.length)) ? logo : null;
}

export const catalogoTaller = z.object({
  /** Accessory ids (of the 45) this workshop does not check. */
  accesoriosOcultos: z.array(z.string()),
  /** Extra zone chips enabled (catalogo.zonas with extra: true). */
  zonasExtra: z.array(z.string()),
});

export const empresa = z.object({ razonSocial: z.string().min(1), rut: z.string() });

export const configGeneral = z.object({
  fases: z.array(
    z.object({
      id: z.string(),
      nombre: z.string(),
      cupo: z.number().int().positive(),
      duracionDias: z.number().int().nonnegative(),
      umbralAtrasoDias: z.number().int().positive(),
    }),
  ),
  empresas: z.array(empresa).min(1),
  direccion: z.string(),
  telefono: z.string().optional(),
  /** Keep GPS in photo EXIF (off by default: privacy). */
  gpsEnFotos: z.boolean(),
  /**
   * Evidence master compression (shared/src/fotos.ts). Optional: older
   * configs lack it and the app then uses the default profile (maxima).
   * The workshop admin only picks the profile; values outside it are for
   * the platform superadmin (enforced by actualizarConfig).
   */
  fotos: configFotos.optional(),
  /**
   * Deprecated: evidence retention is one platform-wide period
   * (RETENCION_EVIDENCIA_DIAS, shared/src/fotos.ts) applied by the bucket
   * lifecycle, not a per-workshop setting. Older configs may still carry it;
   * nothing reads it and actualizarConfig no longer accepts it.
   */
  retencionAnios: z.number().int().positive().nullable().optional(),
  /** Branding for the backoffice and the PDF. Optional: older configs lack it. */
  marca: marcaTaller.optional(),
  /** Per-workshop catalog tweaks over shared/catalogo.json. */
  catalogoTaller: catalogoTaller.optional(),
  /**
   * Custom notice templates (W19) by template id. Placeholders: {cliente},
   * {auto}, {patente}, {fase}, {entrega}, {taller}. Missing id = default text.
   */
  plantillasAvisos: z.record(z.string(), z.string().trim().min(1).max(1000)).optional(),
  /** Legal notices the customer accepts when signing, by type. */
  avisosLegales: avisosLegalesTaller.optional(),
  /**
   * Switch of the customer-facing links: the own expiring link to each signed
   * PDF (shared/src/enlaces.ts) and the tracking page (shared/src/seguimiento.ts).
   * Off unless true. Owner's decisions (2026-10-26):
   * - Only the platform superadmin flips it, per workshop, with the callable
   *   `interruptorEnlacesPdf` (audited as `enlacesPdf.interruptor`). The
   *   workshop admin cannot: actualizarConfig rejects the field and the
   *   rules deny every direct write to config.
   * - Off is a pause: every issued link answers 410 at once, and switching it
   *   back on revives the links still within their validity. To cut one link
   *   for good there is revocarEnlacePdf / revocarSeguimiento.
   * - A link to an inspection that was later annulled answers 410.
   * - The tracking page is valid until the order's delivery + 7 days
   *   (GRACIA_SEGUIMIENTO_DIAS), with a hard ceiling of 180 days per token
   *   (TOPE_SEGUIMIENTO_DIAS) for orders never closed.
   * - The tracking page shows only the last 2 characters of the plate
   *   (patenteParcial).
   */
  enlacesPdf: z.boolean().optional(),
});
export type ConfigGeneral = z.infer<typeof configGeneral>;

// ---- Callable Functions: request contracts ----

const pin = z.string().regex(/^\d{4}$/);

/**
 * A Firestore document id taken from a callable (person uid, device id). Ids
 * are auto-generated or seed slugs; a `/` would address a subcollection, so
 * only safe characters pass.
 */
const idDoc = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);

export const callables = {
  /** Called by a shared device account (rol equipo) the first time it signs in. */
  enrolarDispositivo: z.object({
    nombre: z.string().trim().min(1).max(60),
    /** e.g. "Samsung SM-X216B" (device_info_plus), for the backoffice. */
    modelo: z.string().trim().max(80).optional(),
    /** e.g. "Android 15". */
    plataforma: z.string().trim().max(40).optional(),
  }),
  revocarDispositivo: z.object({ dispositivoId: idDoc }),
  /** Admin approves a pending tablet (enrolled devices start pending). */
  aprobarDispositivo: z.object({ dispositivoId: idDoc }),
  /** Shared device account only: its enrolled device secret proves the tablet. */
  listarOperarios: z.object({ dispositivoId: idDoc, secreto: z.string().min(32) }),
  loginPin: z.object({
    dispositivoId: idDoc,
    secreto: z.string().min(32),
    uid: idDoc,
    pin,
  }),
  crearUsuario: z.object({
    nombre: z.string().trim().min(1).max(60),
    rut: z.string().optional(),
    email: z.email(),
    rol: rolPersona,
    pin: pin.optional(),
    /** Send a branded welcome e-mail with a link to pick their own password. */
    enviarBienvenida: z.boolean().optional(),
  }),
  /** Admin sends the welcome e-mail again (only while the password is temporary). */
  reenviarBienvenida: z.object({ uid: idDoc }),
  /**
   * The caller asks the server to re-check whether its temporary password was
   * already replaced (e.g. through a reset link) and, if so, drop the flag.
   */
  sincronizarClave: z.object({}),
  actualizarUsuario: z.object({
    uid: idDoc,
    nombre: z.string().trim().min(1).max(60).optional(),
    rol: rolPersona.optional(),
    activo: z.boolean().optional(),
  }),
  fijarPin: z.object({ uid: idDoc, pin }),
  quitarPin: z.object({ uid: idDoc }),
  /** New temporary password for a person or a shared device account. */
  restablecerPassword: z.object({ uid: idDoc }),
  /** The caller replaces its temporary password (first sign-in). */
  cambiarPassword: z.object({ password: z.string().min(8).max(128) }),
  /**
   * Public (no session): asks for a branded password reset e-mail. The answer
   * is the same whether the account exists or not.
   */
  solicitarRestablecerClave: z.object({
    email: z.string().trim().toLowerCase().pipe(z.email().max(254)),
  }),
  crearEquipo: z.object({ nombre: z.string().trim().min(1).max(60), email: z.email() }),
  actualizarEquipo: z.object({
    uid: idDoc,
    nombre: z.string().trim().min(1).max(60).optional(),
    activo: z.boolean().optional(),
  }),
  crearTaller: z.object({
    tallerId: z.string().regex(/^[a-z0-9-]{3,40}$/),
    nombre: z.string().min(1),
    empresas: z.array(empresa).min(1),
    direccion: z.string(),
    admin: z.object({ nombre: z.string(), email: z.email(), password: z.string().min(8) }),
  }),
  /**
   * tallerId: only for the platform superadmin (who has no workshop claim);
   * anyone else always edits their own workshop and may not pass another.
   */
  actualizarConfig: configGeneral
    .omit({ retencionAnios: true })
    .partial()
    .extend({ tallerId: z.string().min(1).optional() }),
  /**
   * Platform superadmin turns a lead of tallerza.cl into a customer; the
   * lead's coupon use is counted here, never by the public form. sinCupon:
   * convert without applying a coupon that no longer applies.
   */
  convertirContacto: z.object({ contactoId: idDoc, sinCupon: z.boolean().optional() }),
  /**
   * Platform superadmin only: turns a workshop's `enlacesPdf` switch on or
   * off (see configGeneral.enlacesPdf). Answers { activo, cambiado }.
   */
  interruptorEnlacesPdf: z.object({ tallerId: idDoc, activo: z.boolean() }),
  /**
   * Platform superadmin invites a lead to the free trial in its REAL workshop
   * (shared/src/invitacion.ts): creates it and sends Correo A, or re-sends it
   * with a new temporary password.
   */
  invitarAPrueba: esquemaInvitarAPrueba,
  /**
   * Platform superadmin: correos tipo (shared/src/correosTipo.ts). Save a
   * template or the links they use, and send one to a lead or to a
   * workshop's admin (address, name and links resolved by the server).
   */
  guardarPlantillaCorreo: esquemaGuardarPlantillaCorreo,
  guardarEnlacesCorreo: esquemaGuardarEnlacesCorreo,
  enviarCorreoTipo: esquemaEnviarCorreoTipo,
  /**
   * Platform superadmin saves a workshop's commercial record (plan, pilot,
   * discounts, payment, notes: shared/src/comercial.ts). The whole record is
   * replaced; the callable keeps the previous one in its history.
   */
  guardarFichaComercial: z
    .strictObject({ ...campoFichaComercial, tallerId: z.string().regex(/^[a-z0-9-]{3,40}$/) })
    .superRefine(reglasFicha),
  /**
   * Platform superadmin: how many accounts and devices of the workshop a
   * baja would block now (the confirmation before saving 'terminado').
   */
  resumenBajaTaller: z.strictObject({ tallerId: z.string().regex(/^[a-z0-9-]{3,40}$/) }),
  /**
   * The workshop admin accepts the Terms and Privacy Policy in force
   * (shared/src/terminos.ts) on behalf of the workshop. `version` is the one
   * the admin read: the server rejects any other than TERMINOS_VERSION.
   */
  aceptarTerminos: z.strictObject({ version: versionTerminos }),
  /**
   * avanzar: next phase (neutral zone if full). volver: back to faseIdx with
   * motivo (neutral zone if full). forzar: a car waiting in the neutral zone
   * enters its target phase over the limit. terminar: leaves Entrega.
   * Freed slots pull the FIFO queue in cascade (shared/src/tablero.ts).
   * faseOrigen: the phase the caller saw the order in (its `faseIdx`); the
   * server rejects the move if the order is no longer there, so two people
   * pressing "avanzar" at once advance it one phase, not two. Required: only
   * the backoffice (deployed with the Functions) and the robot call this.
   */
  moverOrden: z.object({
    ordenId: z.string(),
    accion: z.enum(['avanzar', 'volver', 'forzar', 'terminar']),
    faseOrigen: z.number().int().min(0),
    faseIdx: z.number().int().min(0).optional(),
    motivo: z.string().max(300).optional(),
  }),
  /**
   * ZIP with the workshop's data (CSV of inspections, vehicles and orders,
   * plus the signed PDFs). A workshop admin always exports their own
   * workshop; tallerId is only for the platform superadmin.
   */
  exportarDatosTaller: z.object({
    tallerId: z
      .string()
      .regex(/^[a-z0-9-]{3,40}$/)
      .optional(),
  }),
} as const;

export type Callables = { [K in keyof typeof callables]: z.infer<(typeof callables)[K]> };

/** What exportarDatosTaller returns: a short-lived download link. */
export type RespuestaExportarDatos = {
  url: string;
  /** Epoch ms when `url` stops working. */
  expiraEn: number;
  /** Epoch ms when the ZIP was built (older than now when it was reused). */
  generadaEn: number;
  nombreArchivo: string;
  bytes: number;
  /** True when a ZIP built within the rate-limit window was handed out again. */
  reutilizada: boolean;
  conteos: {
    inspecciones: number;
    pdfs: number;
    /** Signed inspections whose PDF is not in Storage (pending or past retention). */
    pdfsFaltantes: number;
    vehiculos: number;
    /** clientes.csv rows (absent in exports built before it existed). */
    clientes?: number;
    ordenes: number;
  };
};
